import React, { useMemo, useState } from 'react'
import { BusinessTripRate } from '@/app/models/configuration/businessTripRatesConfigurationModel'
import { Pagination } from '@/modules/shared/components/Pagination'
import clx from 'classnames'
import styles from './styles.module.scss'
import { useBusinessTripRatesTableContext } from '../context/BusinessTripRatesTableContext'
import { BusinessTripRatesRowProvider } from '../context/BusinessTripRatesRowContext'
import { BusinessTripRatesTableRow } from './BusinessTripRatesTableRow'
import { withDefaultErrorBoundary } from '@/modules/shared/hoc/withDefaultErrorBoundary'
import { TableHeader } from '@/modules/drivers/shared/tableItems/TableHeader'
import { TableHeaderColumn } from '@/modules/drivers/types/table'

const DEFAULT_PER_PAGE = 20
const PINNED_COUNTRY_CODES = ['PL', 'EU'] as const

type SortKey =
  | 'id'
  | 'countryCode'
  | 'dateFrom'
  | 'dateTo'
  | 'allowanceRate'
  | 'allowanceSanitarityRate'
  | 'allowanceHotelLimitationRate'
  | 'allowanceAccomodationRate'

const COLUMNS: TableHeaderColumn[] = [
  { title: 'Państwo', key: 'countryCode', type: 'string', sort: true },
  { title: 'Data od', key: 'dateFrom', type: 'date', sort: true },
  { title: 'Data do', key: 'dateTo', type: 'date', sort: true },
  { title: 'Dieta', key: 'allowanceRate', type: 'number', sort: true },
  {
    title: 'Nocleg',
    key: 'allowanceAccomodationRate',
    type: 'number',
    sort: true,
  },
  {
    title: 'Sanitariaty',
    key: 'allowanceSanitarityRate',
    type: 'number',
    sort: true,
  },
  {
    title: 'Limit hotelowy',
    key: 'allowanceHotelLimitationRate',
    type: 'number',
    sort: true,
  },
  { title: '', key: null, type: 'string', sort: false },
]

const DEFAULT_SORT_KEY: SortKey = 'id'

const getTime = (value: any): number => {
  if (!value) return 0

  if (typeof value.valueOf === 'function') {
    return value.valueOf()
  }

  return new Date(value).getTime()
}

const getPinRank = (rate: BusinessTripRate): number => {
  if (rate.isNew) return -1
  const index = PINNED_COUNTRY_CODES.findIndex(
    (code) => rate.country?.code === code,
  )
  return index === -1 ? PINNED_COUNTRY_CODES.length : index
}

const compareRates = (
  a: BusinessTripRate,
  b: BusinessTripRate,
  sortKey: SortKey,
  sortAsc: boolean,
): number => {
  const pinDiff = getPinRank(a) - getPinRank(b)
  if (pinDiff !== 0) return pinDiff

  let aValue: any
  let bValue: any

  switch (sortKey) {
    case 'id':
      aValue = a.id
      bValue = b.id
      break
    case 'countryCode':
      aValue = a.country?.name || ''
      bValue = b.country?.name || ''
      break
    case 'dateFrom':
      aValue = getTime(a.dateFrom)
      bValue = getTime(b.dateFrom)
      break
    case 'dateTo':
      aValue = getTime(a.dateTo)
      bValue = getTime(b.dateTo)
      break
    case 'allowanceRate':
      aValue = a.allowanceRate
      bValue = b.allowanceRate
      break
    case 'allowanceAccomodationRate':
      aValue = a.allowanceAccomodationRate
      bValue = b.allowanceAccomodationRate
      break
    case 'allowanceHotelLimitationRate':
      aValue = a.allowanceHotelLimitationRate
      bValue = b.allowanceHotelLimitationRate
      break
    case 'allowanceSanitarityRate':
      aValue = a.allowanceSanitarityRate
      bValue = b.allowanceSanitarityRate
      break
    default:
      return 0
  }

  if (typeof aValue === 'number' && typeof bValue === 'number') {
    return sortAsc ? aValue - bValue : bValue - aValue
  }

  const aStr = String(aValue).toLowerCase()
  const bStr = String(bValue).toLowerCase()

  return sortAsc ? aStr.localeCompare(bStr) : bStr.localeCompare(aStr)
}

export const BusinessTripRatesTable = withDefaultErrorBoundary(() => {
  const { rates } = useBusinessTripRatesTableContext()

  const [page, setPage] = useState<number>(1)
  const [perPage, setPerPage] = useState<number>(DEFAULT_PER_PAGE)
  const [sortKey, setSortKey] = useState<SortKey>(DEFAULT_SORT_KEY)
  const [sortAsc, setSortAsc] = useState<boolean>(true)

  const handleHeaderClick = (key: string) => {
    if (sortKey === key) {
      setSortAsc((s) => !s)
    } else {
      setSortKey(key as SortKey)
      setSortAsc(true)
    }
  }

  const rows = useMemo(() => {
    const data = [...rates].sort((a, b) =>
      compareRates(a, b, sortKey, sortAsc),
    )

    const safePage = Math.max(page || 1, 1)
    const safePerPage = Math.max(perPage || data.length || 1, 1)
    const start = (safePage - 1) * safePerPage
    const end = start + safePerPage

    return data.slice(start, end)
  }, [rates, page, perPage, sortKey, sortAsc])

  return rows.length > 0 ? (
    <div className="tw-flex tw-min-h-0 tw-flex-1 tw-flex-col tw-gap-2">
      <div
        className={clx(
          'tw-w-full tw-min-h-0 tw-flex-1 tw-overflow-auto',
          styles.tableWrapper,
        )}
        data-test-id="table-business-trip-rates"
      >
        <table className="tw-w-full tw-text-sm">
          <TableHeader
            columns={COLUMNS}
            disabledCheckboxToggle
            fontSize="sm"
            sortConfig={{ key: sortKey, direction: sortAsc ? 'asc' : 'desc' }}
            onHeaderClick={handleHeaderClick}
          />
          <tbody>
            {rows.map((rate) => (
              <BusinessTripRatesRowProvider key={rate.id} rate={rate}>
                <BusinessTripRatesTableRow />
              </BusinessTripRatesRowProvider>
            ))}
          </tbody>
        </table>
      </div>
      <Pagination
        currentPage={page}
        totalItems={rates.length}
        pageSize={perPage}
        onPageChange={setPage}
        onPageSizeChange={setPerPage}
      />
    </div>
  ) : null
})
